/**
 * Supabase's e-mail links (spec 2026-09-30 akun-dan-keamanan 4 and 5.4: the reset link opens
 * /masuk/sandi-baru, the e-mail change confirms). The TokenHash templates (docs/runbooks/
 * deploy.md) put a one-time hash and its type in the query (`?token_hash=…&type=recovery`,
 * `?token_hash=…&type=email_change`): nothing is spent by opening the link, so a mail scanner
 * that visits it spends nothing; the page spends the hash with one POST (`verifyOtp`) when the
 * person presses Lanjutkan. The type decides the page: the reset link's, or the e-mail
 * change's confirmation page, which needs no sign-in (the link may be opened in a browser where
 * SATU is not signed in). Links mailed before the templates changed (for one release) land with
 * GoTrue's session, or its error, in the URL fragment, as before.
 *
 * Either leaves the address bar and the history entry before the router reads the address and
 * before any lazy chunk loads: a PIN screen, another tab's notice or a slow link never shows a
 * hash or a refresh token in the address. What a page needs is kept in memory for it until that
 * page is on screen (`lihatTautanPemulihan`, `lihatKonfirmasiEmail`, then their `lepas…`);
 * nothing else of SATU reads them. Any other
 * session a fragment carries (an older e-mail change's, on /akun) is signed out at GoTrue at
 * once (`cabut`, GoTrue's local logout of that one session): the browser's own history keeps
 * the original address with its refresh token, and revoking the session is what closes it.
 * DOM-free so the tests import it; ./buangFragmenSesi applies it.
 */
import { bacaTautanPemulihan, type TautanPemulihan } from '../routes/masuk/tujuan'

const KUNCI_SUPABASE = /(^|&)(access_token|refresh_token|error_code|error_description|message)=/
const KOSONG: TautanPemulihan = { jenis: 'kosong' }

/** Where a single-use link's type is spent. */
const HALAMAN_HASH = {
  recovery: '/masuk/sandi-baru',
  email_change: '/masuk/konfirmasi-email',
} as const

interface Lokasi {
  pathname: string
  search: string
  hash: string
}

let tautanPemulihan: TautanPemulihan = KOSONG
let konfirmasiEmail: string | null = null

const halamanSandiBaru = (pathname: string) => pathname.replace(/\/$/, '') === '/masuk/sandi-baru'

/** The address without the fragment, or null to leave it as it is. */
export function alamatTanpaFragmenSesi(lokasi: Lokasi): string | null {
  if (!KUNCI_SUPABASE.test(lokasi.hash.replace(/^#/, ''))) return null
  return `${lokasi.pathname}${lokasi.search}`
}

/**
 * A single-use link's hash in the query: kept for its page by its type, and the address with
 * the hash and the type taken out (on that page). Null when the query carries no hash.
 */
function tanganiHash(lokasi: Lokasi): string | null {
  const query = new URLSearchParams(lokasi.search)
  if (!query.has('token_hash')) return null
  const hash = query.get('token_hash') ?? ''
  const jenis = query.get('type')
  query.delete('token_hash')
  query.delete('type')
  const sisa = query.toString()
  const ke = (pathname: string) => `${pathname}${sisa === '' ? '' : `?${sisa}`}`
  if (hash === '') return ke(lokasi.pathname)
  if (jenis === 'recovery') {
    tautanPemulihan = { jenis: 'hash', tokenHash: hash }
    return ke(HALAMAN_HASH.recovery)
  }
  if (jenis === 'email_change') {
    konfirmasiEmail = hash
    return ke(HALAMAN_HASH.email_change)
  }
  return ke(lokasi.pathname)
}

/**
 * At launch: a single-use link's hash is kept for its page; a fragment's reset session is kept
 * for its page, any other session it carries is revoked (`cabut` receives its access token);
 * then the address is cleared.
 */
export function tanganiFragmenSesi(
  lokasi: Lokasi,
  ganti: (alamat: string) => void,
  cabut: (akses: string) => void,
): void {
  const tanpaHash = tanganiHash(lokasi)
  if (tanpaHash !== null) {
    ganti(tanpaHash)
    return
  }
  const alamat = alamatTanpaFragmenSesi(lokasi)
  if (alamat === null) return
  if (halamanSandiBaru(lokasi.pathname)) {
    tautanPemulihan = bacaTautanPemulihan(lokasi.hash)
  } else {
    const akses = new URLSearchParams(lokasi.hash.replace(/^#/, '')).get('access_token')
    if (akses !== null && akses !== '') cabut(akses)
  }
  ganti(alamat)
}

/**
 * GoTrue's local sign-out of the one session an access token belongs to, as a request that
 * outlives the page (`keepalive`): a tab closing or a link left never waits for it.
 */
export function permintaanCabut(
  url: string,
  kunci: string,
  akses: string,
): { alamat: string; init: RequestInit } {
  return {
    alamat: `${url.replace(/\/+$/, '')}/auth/v1/logout?scope=local`,
    init: {
      method: 'POST',
      keepalive: true,
      headers: { apikey: kunci, Authorization: `Bearer ${akses}` },
    },
  }
}

/**
 * What the address carried for the reset link's page, read while the page renders; it stays
 * until the page is on screen and lets it go (`lepasTautanPemulihan`, routes/masuk/
 * tautanSekali): React may render the page more than once before showing it, and each render
 * must find the link. Opened again after that, the page finds none.
 */
export function lihatTautanPemulihan(): TautanPemulihan {
  return tautanPemulihan
}

/** The reset link's page is on screen: the link is its own now, and no later page finds it. */
export function lepasTautanPemulihan(): void {
  tautanPemulihan = KOSONG
}

/** The e-mail change's hash for its confirmation page, read as the reset link's is; null when none came. */
export function lihatKonfirmasiEmail(): string | null {
  return konfirmasiEmail
}

/** The confirmation page is on screen: no later page finds the hash. */
export function lepasKonfirmasiEmail(): void {
  konfirmasiEmail = null
}
